/** tm/components/CommentSection.tsx — 커뮤니케이션 메모 (수정/삭제/paste 지원) */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Comment } from '../types';
import { C, T, R, Badge, Btn } from './ui';
import * as api from '../api';

/** 첨부 마커 패턴: [첨부: filename.ext](s3key) */
const ATTACH_RE = /\[첨부: ([^\]]+)\]\(([^)]+)\)/g;

function renderContentWithAttachments(text: string) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(ATTACH_RE.source, 'g');
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const fileName = m[1];
    const s3Key = m[2];
    const url = `/api/tm/comments/file/${encodeURIComponent(s3Key)}`;
    const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(fileName);
    const isAudio = /\.(mp3|wav|ogg|m4a|aac|webm)$/i.test(fileName);
    if (isImage) {
      parts.push(
        <div key={m.index} style={{ margin: '4px 0' }}>
          <a href={url} target="_blank" rel="noreferrer">
            <img src={url} alt={fileName} style={{ maxWidth: 200, maxHeight: 200, borderRadius: 4, border: `1px solid ${C.border}` }} />
          </a>
          <div style={{ fontSize: T.xs, color: C.muted }}>{fileName}</div>
        </div>
      );
    } else if (isAudio) {
      parts.push(
        <div key={m.index} style={{ margin: '4px 0' }}>
          <audio controls src={url} style={{ height: 32 }} />
          <div style={{ fontSize: T.xs, color: C.muted }}>{fileName}</div>
        </div>
      );
    } else {
      parts.push(
        <a key={m.index} href={url} target="_blank" rel="noreferrer" style={{ color: C.accent, fontSize: T.xs }}>
          📎 {fileName}
        </a>
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}

interface Props {
  companyId: string;
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

export default function CommentSection({ companyId, onToast }: Props) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [fpVisible, setFpVisible] = useState(false);
  const [custVisible, setCustVisible] = useState(false);   // 고객 페이지 「임지도사의 안내」에 보임 (2026-10-12)
  const [pendingFiles, setPendingFiles] = useState<{ name: string; s3Key: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  async function fetchComments() {
    try {
      const res = await api.getComments(companyId);
      setComments(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { fetchComments(); }, [companyId]);

  // ── 파일 업로드 (파일 객체 직접 처리) ──
  const uploadFile = useCallback(async (file: File): Promise<{ name: string; s3Key: string } | null> => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('company_id', companyId);
      const token = localStorage.getItem('token') || '';
      const res = await fetch('/api/tm/comments/upload-file', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error('업로드 실패');
      const data = await res.json();
      return { name: file.name, s3Key: data.s3_key || data.key || file.name };
    } catch (err: unknown) {
      onToast(err instanceof Error ? err.message : '파일 업로드 실패', 'error');
      return null;
    }
  }, [companyId, onToast]);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    const result = await uploadFile(files[0]);
    if (result) {
      setPendingFiles(prev => [...prev, result]);
      onToast('파일이 첨부되었습니다');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ── Ctrl+V 이미지/파일 붙여넣기 ──
  async function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const f = items[i].getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length === 0) return;
    e.preventDefault();
    setUploading(true);
    for (const file of files) {
      // 클립보드 이미지는 보통 image.png인데, 현재 시각으로 이름 생성
      const ext = (file.type.split('/')[1] || 'png').split(';')[0];
      const renamed = file.name && file.name !== 'image.png'
        ? file
        : new File([file], `clipboard_${Date.now()}.${ext}`, { type: file.type });
      const result = await uploadFile(renamed);
      if (result) {
        setPendingFiles(prev => [...prev, result]);
      }
    }
    setUploading(false);
    onToast('붙여넣기한 파일이 첨부되었습니다');
  }

  async function handleAdd() {
    const fileMarkers = pendingFiles.map(f => `[첨부: ${f.name}](${f.s3Key})`).join('\n');
    const fullContent = [content.trim(), fileMarkers].filter(Boolean).join('\n');
    if (!fullContent) return;
    try {
      await api.createComment({ company_id: companyId, content: fullContent, fp_visible: fpVisible, customer_visible: custVisible });
      setContent('');
      setFpVisible(false);
      setCustVisible(false);
      setPendingFiles([]);
      fetchComments();
      onToast('메모가 추가되었습니다');
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '메모 추가 실패', 'error');
    }
  }

  function startEdit(c: Comment) {
    setEditingId(c.id);
    setEditContent(c.content);
  }

  // 공개 범위 바꾸기 — FP 포털 · 고객 페이지 (2026-10-12)
  async function toggleVisible(c: Comment, key: 'fp_visible' | 'customer_visible') {
    try {
      await api.updateComment(c.id, { [key]: !c[key] } as Partial<Comment>);
      fetchComments();
      onToast(key === 'fp_visible' ? (c.fp_visible ? 'FP 포털에서 숨겼어요' : 'FP 포털에 보여요') : (c.customer_visible ? '고객 페이지에서 숨겼어요' : '고객 페이지 「임지도사의 안내」에 보여요'));
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '바꾸지 못했어요', 'error');
    }
  }

  function cancelEdit() {
    setEditingId(null);
    setEditContent('');
  }

  async function saveEdit() {
    if (!editingId || !editContent.trim()) return;
    try {
      await api.updateComment(editingId, { content: editContent.trim() });
      setEditingId(null);
      setEditContent('');
      fetchComments();
      onToast('메모가 수정되었습니다');
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '메모 수정 실패', 'error');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('이 메모를 삭제하시겠습니까? 복구할 수 없습니다.')) return;
    try {
      await api.deleteComment(id);
      fetchComments();
      onToast('메모가 삭제되었습니다');
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '메모 삭제 실패', 'error');
    }
  }

  function fmtTime(ts: string): string {
    if (!ts) return '';
    const d = new Date(ts);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  return (
    <div>
      {/* 입력 영역 */}
      <div style={{ marginBottom: 12 }}>
        <textarea ref={textareaRef} value={content} onChange={e => setContent(e.target.value)}
          onPaste={handlePaste}
          placeholder="메모를 입력하세요 (이미지는 Ctrl+V로 바로 붙여넣기 가능)"
          rows={2} style={{
            width: '100%', padding: '10px 12px', borderRadius: R.md,
            border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none',
            resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6,
          }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 6 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: T.xs, color: C.sub, cursor: 'pointer' }} title="FP 포털 「임지도사 메모」에 보여요">
              <input type="checkbox" checked={fpVisible} onChange={e => setFpVisible(e.target.checked)} />FP 공개
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: T.xs, color: C.sub, cursor: 'pointer', marginRight: 4 }} title="고객 페이지 「임지도사의 안내」에 보여요">
              <input type="checkbox" checked={custVisible} onChange={e => setCustVisible(e.target.checked)} />고객 공개
            </label>
            <input ref={fileInputRef} type="file"
              style={{ display: 'none' }} onChange={handleFileSelect} />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              style={{ fontSize: T.xs, padding: '6px 10px', borderRadius: R.sm,
                border: `1px solid ${C.border}`, background: 'transparent',
                color: C.muted, cursor: 'pointer' }}
              title="파일 첨부">
              {uploading ? '⏳' : '📎'}
            </button>
            <Btn onClick={handleAdd} disabled={!content.trim() && pendingFiles.length === 0}
              style={{ fontSize: T.xs, padding: '6px 16px' }}>💬 추가</Btn>
          </div>
        </div>
        {pendingFiles.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
            {pendingFiles.map((f, i) => (
              <span key={i} style={{ fontSize: T.xs, padding: '2px 6px', borderRadius: R.sm,
                background: C.accentLt, color: C.accent, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                📎 {f.name}
                <span onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                  style={{ cursor: 'pointer', color: C.red, fontWeight: 700 }}>×</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 메모 목록 */}
      {loading && <div style={{ padding: 12, textAlign: 'center', color: C.muted, fontSize: T.xs }}>로딩 중...</div>}
      {!loading && comments.length === 0 && (
        <div style={{ padding: '2px 2px', color: C.muted, fontSize: T.sm }}>
          아직 메모가 없어요
        </div>
      )}
      {comments.map(c => (
        <div key={c.id} style={{
          padding: '10px 12px', borderRadius: R.md,
          border: `1px solid ${C.border}`, marginBottom: 6,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: T.xs, fontWeight: 600, color: C.sub }}>
              {c.author || '관리자'}
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {c.sms_sent && <Badge bg={C.greenLt} color={C.green}>SMS</Badge>}
              {(['fp_visible', 'customer_visible'] as const).map(k => (
                <button key={k} onClick={() => toggleVisible(c, k)} title={c[k] ? '누르면 숨겨요' : '누르면 보여요'}
                  style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, cursor: 'pointer', fontWeight: 600,
                    border: `1px solid ${c[k] ? (k === 'fp_visible' ? '#7C3AED' : '#0A7D74') : C.border}`,
                    background: c[k] ? (k === 'fp_visible' ? '#F5F3FF' : '#E1F8F5') : C.white,
                    color: c[k] ? (k === 'fp_visible' ? '#7C3AED' : '#0A7D74') : C.muted }}>
                  {k === 'fp_visible' ? 'FP 공개' : '고객 공개'}
                </button>
              ))}
              <span style={{ fontSize: T.xs, color: C.muted }}>{fmtTime(c.created_at)}</span>
              {editingId !== c.id && (
                <>
                  <button onClick={() => startEdit(c)} title="수정"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: C.muted, padding: '2px 4px' }}>✏️</button>
                  <button onClick={() => handleDelete(c.id)} title="삭제"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: C.red, padding: '2px 4px' }}>🗑</button>
                </>
              )}
            </div>
          </div>
          {editingId === c.id ? (
            <div>
              <textarea value={editContent} onChange={e => setEditContent(e.target.value)}
                rows={3} style={{
                  width: '100%', padding: '8px 10px', borderRadius: R.sm,
                  border: `1px solid ${C.accent}`, fontSize: T.sm, outline: 'none',
                  resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6, marginBottom: 4,
                }} />
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button onClick={cancelEdit}
                  style={{ fontSize: T.xs, padding: '4px 10px', borderRadius: R.sm,
                    border: `1px solid ${C.border}`, background: C.white, color: C.muted, cursor: 'pointer' }}>
                  취소
                </button>
                <button onClick={saveEdit} disabled={!editContent.trim()}
                  style={{ fontSize: T.xs, padding: '4px 12px', borderRadius: R.sm,
                    border: 'none', background: C.accent, color: C.white, fontWeight: 600, cursor: 'pointer' }}>
                  저장
                </button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: T.sm, color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {renderContentWithAttachments(c.content)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
